import { api, PartipostApiResponse, PartipostApiPaginationWrapper } from './'
import {
    TeamState,
    CreateTeamRequestPayload,
    UpdateTeamRequestPayload,
    UpdateTeamTypeRequestPayload,
    MemberState,
    CreateMemberRequestPayload,
    UpdateMemberRequestPayload,
    TeamListRequestPayload,
    BaseListRequest,
    TeamsResponse,
    MembersResponse,
    BaseTeamPayload,
    CreateTeamBrandRequestPayload,
    UpdateTeamBrandRequestPayload,
    BaseTeamBrandMemberPayload,
    AddRolePayload,
    DeleteRolePayload,
    UpdateRolePayload,
    UpdateRolesPermissionsPayload,
    UpdateBusinessDetailsPayload,
    BaseBusinessDetails,
    WalletState,
    GetWalletTransactionsPayload,
    WalletTransaction,
    TRANSACTIONS_PER_PAGE,
} from '../../state/teams'
import { BrandsResponse, CreateBrandResponse } from './brands'
import { UpdateOnboardingOptionsPayload } from '../../state/teams'

export const getTeams = (payload: BaseListRequest) => {
    const { url, teamFilter, ...rest } = payload
    const baseUrl = url ? url.replace('http:', 'https:') : teamFilter ? `teams/${teamFilter}/` : 'teams/'
    return api.get<PartipostApiResponse<TeamsResponse>>(baseUrl, rest)
}

export const getTeam = (payload: BaseTeamPayload) => {
    return api.get<PartipostApiResponse<TeamState>>(`teams/${payload.teamUuid}/`)
}

export const createTeam = (payload: CreateTeamRequestPayload) => {
    return api.post<PartipostApiResponse<TeamState>>('teams/', payload)
}

export const updateTeam = (payload: UpdateTeamRequestPayload) => {
    const { teamUuid, ...rest } = payload
    return api.put<PartipostApiResponse<TeamState>>(`teams/${teamUuid}/`, rest)
}

export const updateTeamType = (payload: UpdateTeamTypeRequestPayload) => {
    const { teamUuid, ...rest } = payload
    return api.patch<PartipostApiResponse<TeamState>>(`teams/${teamUuid}/settings/`, rest)
}

export const deleteTeam = (payload: BaseTeamPayload) => {
    const { teamUuid } = payload
    return api.delete<PartipostApiResponse<BaseTeamPayload>>(`teams/${teamUuid}/`)
}

export const createMember = (payload: CreateMemberRequestPayload) => {
    const { teamUuid, ...rest } = payload
    return api.post<PartipostApiResponse<MemberState>>(`teams/${teamUuid}/members/`, rest)
}

export const updateMember = (payload: UpdateMemberRequestPayload) => {
    const { teamUuid, memberUuid, ...rest } = payload
    return api.put<PartipostApiResponse<MemberState>>(`teams/${teamUuid}/members/${memberUuid}/`, rest)
}

export const deleteMember = (payload: BaseTeamBrandMemberPayload) => {
    const { memberUuid } = payload
    return api.delete<PartipostApiResponse>(`/members/${memberUuid}/`)
}

export const removeTeamMember = (payload: BaseTeamBrandMemberPayload) => {
    const { teamUuid, memberUuid } = payload
    return api.delete<PartipostApiResponse>(`teams/${teamUuid}/members/${memberUuid}/`)
}

export const getTeamMembers = (payload: TeamListRequestPayload) => {
    const { teamUuid, url, ...rest } = payload
    const baseUrl = url ? url.replace('http:', 'https:') : `teams/${teamUuid}/members/`
    return api.get<PartipostApiResponse<MembersResponse>>(baseUrl, rest)
}

export const resendMemberInvite = (payload: BaseTeamBrandMemberPayload) => {
    const { teamUuid, memberUuid } = payload
    return api.patch<PartipostApiResponse>(`teams/${teamUuid}/members/${memberUuid}/send-invite/`)
}

export const getTeamBrands = (payload: TeamListRequestPayload) => {
    const { teamUuid, url, ...rest } = payload
    const baseUrl = url ? url.replace('http:', 'https:') : `teams/${teamUuid}/brands/`
    return api.get<PartipostApiResponse<BrandsResponse>>(baseUrl, rest)
}

export const getBrand = (payload: BaseTeamBrandMemberPayload) => {
    const { brandUuid } = payload
    return api.get<PartipostApiResponse<CreateBrandResponse>>(`brands/${brandUuid}/`)
}

export const createBrand = (payload: CreateTeamBrandRequestPayload) => {
    const { teamUuid, ...rest } = payload
    return api.post<PartipostApiResponse<CreateBrandResponse>>(`teams/${teamUuid}/brands/`, rest)
}

export const updateBrand = (payload: UpdateTeamBrandRequestPayload) => {
    const { teamUuid, uuid, ...rest } = payload
    return api.patch<PartipostApiResponse<CreateBrandResponse>>(`teams/${teamUuid}/brands/${uuid}/`, rest)
}

export const deleteBrand = (payload: BaseTeamBrandMemberPayload) => {
    const { brandUuid, teamUuid } = payload
    return api.delete<PartipostApiResponse>(`teams/${teamUuid}/brands/${brandUuid}/`)
}

export const getRoles = (payload: BaseTeamPayload) => {
    const { teamUuid } = payload
    return api.get<PartipostApiResponse<any>>(`teams/${teamUuid}/roles/`)
}

export const getPermissions = (payload: BaseTeamPayload) => {
    const { teamUuid } = payload
    return api.get<PartipostApiResponse<any>>(`teams/${teamUuid}/permission-groups/`)
}

export const addRole = (payload: AddRolePayload) => {
    const { name, teamUuid } = payload
    return api.post<PartipostApiResponse<any>>(`/teams/${teamUuid}/roles/`, { name: name })
}
export const deleteRole = (payload: DeleteRolePayload) => {
    const { teamUuid, roleId } = payload
    return api.delete<PartipostApiResponse<any>>(`/teams/${teamUuid}/roles/${roleId}/`)
}
export const editRole = (payload: UpdateRolePayload) => {
    const { teamUuid, roleId, roleName } = payload
    return api.patch<PartipostApiResponse<any>>(`/teams/${teamUuid}/roles/${roleId}/`, { name: roleName })
}
export const updateRolesPermissions = (payload: UpdateRolesPermissionsPayload) => {
    const { teamUuid, teamRoles } = payload
    return api.put<PartipostApiResponse<any>>(`/teams/${teamUuid}/roles/permission-assignment/`, teamRoles)
}

export const updateBusinessDetails = (payload: UpdateBusinessDetailsPayload) => {
    const { teamUuid, ...rest } = payload
    return api.put<PartipostApiResponse<BaseBusinessDetails>>(`/teams/${teamUuid}/billing/`, rest)
}

export const getBusinessDetails = (payload: BaseTeamPayload) => {
    const { teamUuid, ...rest } = payload
    return api.get<PartipostApiResponse<BaseBusinessDetails>>(`/teams/${teamUuid}/billing/`, rest)
}

export const getTeamWallets = (payload: BaseTeamPayload) => {
    return api.get<PartipostApiResponse<WalletState[]>>(`teams/${payload.teamUuid}/wallets/`)
}

export const getWalletTransactions = (payload: GetWalletTransactionsPayload) => {
    const { walletUuid, campaignName, balanceType, page = 1, limit = TRANSACTIONS_PER_PAGE } = payload
    const params = {
        limit,
        offset: (page - 1) * limit,
        ...(campaignName ? { campaign_name: campaignName } : {}),
        ...(balanceType ? { balance_type: balanceType } : {}),
    }
    return api.get<PartipostApiResponse<PartipostApiPaginationWrapper<WalletTransaction[]>>>(
        `ewallets/${walletUuid}/transactions/`,
        params,
    )
}

export const updateOnboardingOptions = (payload: UpdateOnboardingOptionsPayload) => {
    const { teamUuid, onSuccessCallback, ...rest } = payload
    const sanitizedPayload = Object.entries(rest).reduce<Record<string, string | string[]>>((acc, [key, value]) => {
        if (typeof value === 'string' && value.trim() === '') {
            return acc
        }

        if (Array.isArray(value) && value.length === 0) {
            return acc
        }

        if (typeof value === 'string' || Array.isArray(value)) {
            acc[key] = value
        }

        return acc
    }, {})

    return api.patch<PartipostApiResponse<TeamState>>(`/teams/${teamUuid}/onboarding-options/`, sanitizedPayload)
}
